import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  mergeStyleSets,
} from "@fluentui/react";
import { CaretLeft, Robot, HardHat, MapPin, Calendar, LockSimple } from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import { getPhosphorIcon } from "../utils/iconMap";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Types ─────────────────────────────────────────
interface Sa2Area { sa2_code: string; sa2_name: string; area_sqkm: number }

// ── Constants ─────────────────────────────────────────
const COLORS = ["#F59E0B","#38BDF8","#34D399","#FBBF24","#F472B6","#A78BFA","#FB923C","#06B6D4","#E11D48","#8B5CF6","#10B981","#6366F1","#14B8A6","#D946EF"];

const FY_OPTIONS: IDropdownOption[] = [
  { key: "2024-25", text: "2024-25" },
  { key: "2023-24", text: "2023-24" },
  { key: "2022-23", text: "2022-23" },
];

const CATEGORY_OPTIONS: IDropdownOption[] = [
  { key: "all", text: "All Categories" },
  { key: "Roads", text: "Roads" },
  { key: "Drainage", text: "Drainage" },
  { key: "Buildings", text: "Buildings" },
  { key: "Bridges", text: "Bridges" },
  { key: "Footpaths", text: "Footpaths" },
  { key: "Parks", text: "Parks" },
];

const STATUS_OPTIONS: IDropdownOption[] = [
  { key: "all", text: "All Statuses" },
  { key: "Planned", text: "Planned" },
  { key: "In Progress", text: "In Progress" },
  { key: "Completed", text: "Completed" },
  { key: "Deferred", text: "Deferred" },
];

const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ───────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function fmtCurrency(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  if (isNaN(n)) return "-";
  if (Math.abs(n) >= 1_000_000_000) return "$" + (n / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(n) >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return "$" + (n / 1_000).toFixed(0) + "K";
  return "$" + n.toLocaleString("en-AU");
}
function fmtPct(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toFixed(1) + "%";
}
function truncLabel(s: string, max = 18): string {
  return s.length > max ? s.slice(0, max) + "..." : s;
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + "K";
  return String(value);
}

// ── Styles ───────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterRow: {
    display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  } as any,
  filterLabel: { fontSize: 12, fontWeight: 600, color: p.text2, marginBottom: 4 },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  tableWrap: {
    background: "#fff", borderRadius: 10, padding: 20, marginBottom: 28,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto",
  } as any,
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 } as any,
  th: { textAlign: "left" as any, padding: "8px 10px", borderBottom: "2px solid #E1E1E1", color: p.text2, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" as any },
  td: { padding: "7px 10px", borderBottom: "1px solid #F0F0F0", color: p.text, whiteSpace: "nowrap" as any },
  agentCta: {
    background: "linear-gradient(135deg, #D97706, #FBBF24)", borderRadius: 12, padding: "24px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
    transition: "transform 0.15s ease", ":hover": { transform: "translateY(-2px)" },
  } as any,
  agentCtaText: { color: "#fff" },
  agentCtaTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  agentCtaSub: { fontSize: 13, opacity: 0.85 },
  agentCtaIcon: { fontSize: 32, color: "#fff", opacity: 0.9 },
  error: { marginBottom: 20 },
});

// ══════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════
export default function CapitalWorks() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mainData, setMainData] = useState<any[]>([]);
  const [sa2Areas, setSa2Areas] = useState<Sa2Area[]>([]);
  const [comparisonData, setComparisonData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);

  const [fy, setFy] = useState("2024-25");
  const [sa2, setSa2] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (sa2 !== "all") params.set("sa2", sa2);
      if (category !== "all") params.set("category", category);
      if (status !== "all") params.set("status", status);
      const [mainRes, sa2Res, compRes, trendRes] = await Promise.all([
        accessService.get(`/capital-works/projects?${params}`),
        accessService.get("/abs/sa2-areas"),
        accessService.get(`/capital-works/comparison?fy=${fy}&metric=actual_spend`),
        accessService.get(`/capital-works/trend?metric=budget`),
      ]);
      setMainData(mainRes.data);
      setSa2Areas(sa2Res.data);
      setComparisonData(compRes.data);
      setTrendData(trendRes.data);
    } catch (err: any) {
      setError(err.message || "Failed to load capital works data");
    } finally {
      setLoading(false);
    }
  }, [fy, sa2, category, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Dropdown options ──
  const sa2Options: IDropdownOption[] = useMemo(() => [
    { key: "all", text: "All Boroondara" },
    ...sa2Areas.map(a => ({ key: a.sa2_code, text: a.sa2_name })),
  ], [sa2Areas]);

  // ── KPIs ──
  const kpis = useMemo(() => {
    if (mainData.length === 0) return null;
    const totalProjects = mainData.length;
    const totalBudget = mainData.reduce((s: number, r: any) => s + (Number(r.budget) || 0), 0);
    const totalSpend = mainData.reduce((s: number, r: any) => s + (Number(r.actual_spend) || 0), 0);
    const completed = mainData.filter((r: any) => r.status === "Completed").length;
    const completionRate = totalProjects > 0 ? (completed / totalProjects) * 100 : 0;
    return { totalProjects, totalBudget, totalSpend, completionRate };
  }, [mainData]);

  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { icon: "ConstructionCone", color: "#D97706", label: "Total Projects", value: fmtNum(kpis.totalProjects), sub: `FY ${fy}` },
      { icon: "Money", color: "#38BDF8", label: "Total Budget", value: fmtCurrency(kpis.totalBudget), sub: `FY ${fy}` },
      { icon: "PaymentCard", color: "#34D399", label: "Total Spend", value: fmtCurrency(kpis.totalSpend), sub: `FY ${fy}` },
      { icon: "CheckMark", color: "#F472B6", label: "Completion Rate", value: fmtPct(kpis.completionRate), sub: "of all projects" },
    ];
  }, [kpis, fy]);

  // ── Chart data ──
  const budgetVsActual = useMemo(() => {
    const byCat: Record<string, { category: string; budget: number; actual_spend: number }> = {};
    mainData.forEach((r: any) => {
      const cat = r.category || "Other";
      if (!byCat[cat]) byCat[cat] = { category: cat, budget: 0, actual_spend: 0 };
      byCat[cat].budget += Number(r.budget) || 0;
      byCat[cat].actual_spend += Number(r.actual_spend) || 0;
    });
    return Object.values(byCat).sort((a, b) => b.budget - a.budget);
  }, [mainData]);

  const statusPie = useMemo(() => {
    const byStatus: Record<string, number> = {};
    mainData.forEach((r: any) => {
      const s = r.status || "Unknown";
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    return Object.entries(byStatus).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
  }, [mainData]);

  const spendBySa2 = useMemo(() => {
    return comparisonData
      .map((r: any) => ({ name: r.label || r.sa2_name, value: Number(r.value || r.actual_spend) || 0 }))
      .sort((a: any, b: any) => b.value - a.value);
  }, [comparisonData]);

  const budgetTrend = useMemo(() => {
    return trendData.map((r: any) => ({ financial_year: r.financial_year, value: Number(r.value || r.budget) || 0 }));
  }, [trendData]);

  // ══════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════
  if (loading && mainData.length === 0) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading capital works data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav('/dashboards')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: '#D97706', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* ── Header ── */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Capital Works</div>
          <div className={styles.subtitle}>
            Project budgets, actual spend, completion status and capital works across {sa2Areas.length} SA2 areas.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><HardHat size={12} weight="regular" /> Capital Works</span>
            <span className={styles.chip}><MapPin size={12} weight="regular" /> {sa2Areas.length} SA2 Areas</span>
            <span className={styles.chip}><Calendar size={12} weight="regular" /> FY 2022-23 to 2024-25</span>
            <span className={styles.chip}><LockSimple size={12} weight="regular" /> OFFICIAL: Sensitive</span>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* ── Filters ── */}
        <div className={styles.filterRow}>
          <div>
            <div className={styles.filterLabel}>Financial Year</div>
            <Dropdown
              selectedKey={fy}
              options={FY_OPTIONS}
              onChange={(_, opt) => opt && setFy(String(opt.key))}
              styles={{ root: { width: 160 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>SA2 Area</div>
            <Dropdown
              selectedKey={sa2}
              options={sa2Options}
              onChange={(_, opt) => opt && setSa2(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Category</div>
            <Dropdown
              selectedKey={category}
              options={CATEGORY_OPTIONS}
              onChange={(_, opt) => opt && setCategory(String(opt.key))}
              styles={{ root: { width: 180 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Status</div>
            <Dropdown
              selectedKey={status}
              options={STATUS_OPTIONS}
              onChange={(_, opt) => opt && setStatus(String(opt.key))}
              styles={{ root: { width: 160 } }}
            />
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={styles.kpiCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={getPhosphorIcon(kpi.icon)} size="sm" color={kpi.color} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.color }}>{kpi.value}</div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className={styles.chartsRow}>
          {/* Grouped Bar: Budget vs Actual by Category */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Budget vs Actual by Category ({fy})</div>
            {budgetVsActual.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={budgetVsActual} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="category" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="budget" name="Budget" fill="#D97706" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actual_spend" name="Actual Spend" fill="#38BDF8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No budget data available
              </div>
            )}
          </div>

          {/* Pie: Project Status Distribution */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Project Status Distribution ({fy})</div>
            {statusPie.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true} fontSize={11}>
                    {statusPie.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No status data available
              </div>
            )}
          </div>
        </div>

        <div className={styles.chartsRow}>
          {/* Bar: Spend by SA2 */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Actual Spend by SA2 ({fy})</div>
            {spendBySa2.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={spendBySa2} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                  <Bar dataKey="value" name="Actual Spend" fill="#34D399" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No spend data available
              </div>
            )}
          </div>

          {/* Line: Budget Trend */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Budget Trend</div>
            {budgetTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={budgetTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="financial_year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                  <Line type="monotone" dataKey="value" name="Total Budget" stroke="#D97706" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No trend data available
              </div>
            )}
          </div>
        </div>

        {/* ── Data Table ── */}
        <div className={styles.tableWrap}>
          <div className={styles.chartTitle}>Capital Works Projects — FY {fy}</div>
          {mainData.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Suburb</th>
                  <th className={styles.th}>Ref</th>
                  <th className={styles.th}>Project</th>
                  <th className={styles.th}>Category</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Budget</th>
                  <th className={styles.th}>Spend</th>
                  <th className={styles.th}>Start</th>
                  <th className={styles.th}>Target</th>
                  <th className={styles.th}>FY</th>
                </tr>
              </thead>
              <tbody>
                {mainData.map((row: any, i: number) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                    <td className={styles.td}>{row.sa2_name}</td>
                    <td className={styles.td}>{row.project_ref}</td>
                    <td className={styles.td}>{truncLabel(row.project_name || "", 30)}</td>
                    <td className={styles.td}>{row.category}</td>
                    <td className={styles.td}>{row.status}</td>
                    <td className={styles.td}>{fmtCurrency(row.budget)}</td>
                    <td className={styles.td}>{fmtCurrency(row.actual_spend)}</td>
                    <td className={styles.td}>{row.start_date}</td>
                    <td className={styles.td}>{row.target_completion}</td>
                    <td className={styles.td}>{row.financial_year}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: p.text3 }}>
              No capital works data available for the selected filters.
            </div>
          )}
        </div>

        {/* ── Agent CTA ── */}
        <div className={styles.agentCta} onClick={() => nav("/chat")} role="button" tabIndex={0}>
          <div className={styles.agentCtaText}>
            <div className={styles.agentCtaTitle}>Ask the Capital Works Agent</div>
            <div className={styles.agentCtaSub}>
              Get instant answers about project budgets, spend, timelines and completion status across Boroondara.
            </div>
          </div>
          <Robot size={32} weight="duotone" color="#fff" style={{ opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
}
